'use client';
import * as React from 'react';
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';
import { cn } from '../cn';

export type ToastKind = 'success' | 'error' | 'info';
export interface ToastMessage {
  id: number;
  kind: ToastKind;
  title: string;
  description?: string;
  duration?: number;
}
interface ToastApi {
  toast: (t: Omit<ToastMessage, 'id'>) => void;
  success: (title: string, description?: string) => void;
  error: (title: string, description?: string) => void;
  info: (title: string, description?: string) => void;
}
const ToastContext = React.createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const ctx = React.useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = React.useState<ToastMessage[]>([]);
  const counter = React.useRef(0);
  const dismiss = React.useCallback((id: number) => setItems((xs) => xs.filter((x) => x.id !== id)), []);
  const toast = React.useCallback(
    (t: Omit<ToastMessage, 'id'>) => {
      const id = ++counter.current;
      setItems((xs) => [...xs.slice(-3), { ...t, id }]);
      window.setTimeout(() => dismiss(id), t.duration ?? (t.kind === 'error' ? 6000 : 3500));
    },
    [dismiss],
  );
  const api = React.useMemo<ToastApi>(
    () => ({
      toast,
      success: (title, description) => toast({ kind: 'success', title, description }),
      error: (title, description) => toast({ kind: 'error', title, description }),
      info: (title, description) => toast({ kind: 'info', title, description }),
    }),
    [toast],
  );
  const icons = { success: <CheckCircle2 className="size-4 text-green" />, error: <AlertCircle className="size-4 text-red" />, info: <Info className="size-4 text-accent" /> };
  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-4 z-[80] flex flex-col items-center gap-2 px-4">
        {items.map((t) => (
          <div key={t.id} role="status" className={cn('ui-toast glass pointer-events-auto flex w-full max-w-[420px] items-start gap-3 rounded-[var(--radius-lg)] px-4 py-3 shadow-[var(--shadow-lg)]')}>
            <span className="mt-0.5 shrink-0">{icons[t.kind]}</span>
            <div className="min-w-0 flex-1">
              <div className="text-[13.5px] font-medium text-text">{t.title}</div>
              {t.description ? <div className="text-[12.5px] text-text-2 mt-0.5 break-words">{t.description}</div> : null}
            </div>
            <button type="button" aria-label="Dismiss" onClick={() => dismiss(t.id)} className="shrink-0 rounded-full p-1 text-text-3 hover:bg-fill-3 hover:text-text">
              <X className="size-3.5" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
